import { createThread } from "../../../services/restAPI/endpoints/ChatBotAPI";
import { UserType } from "../../../types/api";

export interface Thread {
   id: string;
   object: string;
   created_at: number;
   metadata: Record<string, any>;
}

export interface SelectedBotUser {
   firstName: string;
   id: string;
   lastName: string;
   oldId: string;
   userType: string;
   key: string;
}

export interface CreateThreadResponse {
   currentThread: Thread;
   selectedBotUser: SelectedBotUser;
}

export const checkMailReferences = (data: any) => {
   if (data?.references?.length > 0) {
      return data.references[0];
   } else {
      return data?.messageIdFromHeader;
   }
};

export const findMailToSave = (
   content: string | undefined,
   mailData: any,
   data: any,
   createChatAndPushMessage: (value: string) => void
) => {
   let finalValue;

   if (content === undefined) {
      // Checking the mail content only if it hasn't been changed
      const mailBody = mailData.mailDetails.find((mailObj) => mailObj.id === data.Id).mailBody;

      if (mailBody.find((mailType) => mailType?.type === "textHtml")) {
         // If there is an object of type "textHtml" in the mail, use its content
         const htmlMail = mailBody.find((mailType) => mailType?.type === "textHtml");
         finalValue = htmlMail.content;
      } else {
         // Otherwise, use the content of the first object in mailBody (if it exists)
         finalValue = mailBody?.[0]?.content;
      }
   } else {
      // If the content has been changed, use the new content
      finalValue = content;
   }
   createChatAndPushMessage(finalValue);
};

/**
 * Sorts the list of users by user type. So far, it returns in the firs place, users with "userType" field
 * @param {Array} filteredUsers - The list of users to be sorted.
 * @returns {Array} - The sorted list of users.
 */
export const sortUsersByUserType = (filteredUsers) => {
   const compareFunction = (a, b) => {
      if (a.userType && !b.userType) {
         return -1;
      } else if (!a.userType && b.userType) {
         return 1;
      } else {
         return 0;
      }
   };

   filteredUsers.sort(compareFunction);

   return filteredUsers;
};

/**
 * Returns an array of users with "bot" type from the provided user list.
 * @param usersList The list of users to search through.
 * @returns An array of bot users.
 */

export const getBotUsers = (usersList) => {
   if (usersList.filter((user) => user.userType === "bot")) {
      return usersList.filter((user) => user.userType === "bot");
   } else return [];
};

/**
 * Returns an array of preselected user IDs based on the provided user list and old ID.
 * @param usersList The list of users to search through.
 * @param oldId The old ID to include as a preselected user.
 * @param isCase By this boolean value we check if we should add AI bot to the room.
 * @returns An array of preselected user IDs.
 */

export const getPreselectedUsers = (usersList, oldId: number, isCase): number[] => {
   if (usersList && oldId) {
      let preselectedUsers: number[] = [oldId];

      // so far we only adding a specyfic AI asystent to all of the rooms created from mail.
      // bot`s reference (for example: Mailomistrz) are the same in all of the domains(microservice + one independent database).
      if (!isCase) {
         const botUsers = getBotUsers(usersList);

         if (botUsers?.find((user) => user.firstName === "Mailomistrz")) {
            preselectedUsers?.push(
               botUsers?.find((user) => user.firstName === "Mailomistrz")?.oldId
            );
         }
      }

      return preselectedUsers;
   } else {
      return [];
   }
};

/**
 * Function to find a user of type "bot" based on the provided data.
 * @param {Object[]} chatData - An array of chat data.
 * @param {number[]} selectedUsers - An array of selected user IDs.
 * @returns {Object | null} - The found "bot" user or null if the user is not found.
 */
export const findSelectedBotUser = (chatData: any[], selectedUsers: number[]): any | null => {
   if (chatData && selectedUsers) {
      for (const user of chatData) {
         if (user.userType === "bot") {
            if (selectedUsers.includes(user.id)) {
               return user;
            }
         }
      }
   }
   // If the appropriate user is not found, return null
   return null;
};

/**
 * Retrieves AI data from java API (createThread()), and also returns complete assistent object. We use those data(thread data / AI asystent data) to create new room
 * @param {Object[]} users - An array of user objects.
 * @param {number[]} selectedUsers - An array of selected user IDs.
 * @returns {Object | undefined} - An object containing the current thread and selected bot user, or undefined if no bot user is found.
 */

export const getAiData = async (
   users: any[],
   selectedUsers: number[]
): Promise<{ currentThread: any; selectedBotUser: any } | undefined> => {
   let currentThread;

   const selectedBotUser = findSelectedBotUser(users, selectedUsers);

   currentThread = await createThread();
   if (selectedBotUser) {
      try {
         return { currentThread: currentThread.data, selectedBotUser: selectedBotUser };
      } catch (err) {
         console.log(err);
         return undefined;
      }
   } else {
      return currentThread;
   }
};
